import { Link } from '@inertiajs/react';
import type { Adventure, AdventureReward } from '@/types/adventures';

interface AdventuresTabProps {
    adventures: Adventure[];
    hexId: number;
    inProgressCount: number;
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
    onTabChange: (tab: string) => void;
}

/**
 * Get a pseudo-random rotation for each adventure card based on its ID.
 * Creates variety while being deterministic.
 */
function getCardRotation(id: number): number {
    // Use ID to generate a predictable "random" rotation between -3 and 3 degrees
    const seed = (id * 7) % 13;
    return (seed - 6) * 0.5;
}

/**
 * Get difficulty info (label, color, ribbon color)
 */
function getDifficultyInfo(totalDifficulty: number): {
    label: string;
    ribbonColor: string;
    textColor: string;
} {
    if (totalDifficulty < 15) {
        return { label: 'Easy', ribbonColor: '#22c55e', textColor: '#fff' };
    }
    if (totalDifficulty < 35) {
        return { label: 'Normal', ribbonColor: '#3b82f6', textColor: '#fff' };
    }
    if (totalDifficulty < 55) {
        return { label: 'Hard', ribbonColor: '#eab308', textColor: '#000' };
    }
    if (totalDifficulty < 75) {
        return { label: 'Very Hard', ribbonColor: '#f97316', textColor: '#fff' };
    }
    if (totalDifficulty < 100) {
        return { label: 'Insane', ribbonColor: '#ef4444', textColor: '#fff' };
    }
    return { label: 'Impossible', ribbonColor: '#7c3aed', textColor: '#fff' };
}

/**
 * Format reward for display
 */
function formatReward(reward: AdventureReward): string {
    const type = reward.reward_type.value;
    if (type === 'item') {
        return reward.item_recipe?.name || 'Item';
    }
    if (type === 'blueprint') {
        return `Blueprint: ${reward.item_recipe?.name || 'Unknown'}`;
    }
    if (type === 'material') {
        const qty = reward.quantity_min === reward.quantity_max
            ? reward.quantity_min
            : `${reward.quantity_min}-${reward.quantity_max}`;
        return `${qty}x ${reward.material_type}`;
    }
    if (type === 'xp') {
        const qty = reward.quantity_min === reward.quantity_max
            ? reward.quantity_min
            : `${reward.quantity_min}-${reward.quantity_max}`;
        return `${qty} XP`;
    }
    if (type === 'reputation') {
        const amt = reward.reputation_min === reward.reputation_max
            ? `+${reward.reputation_min}`
            : `+${reward.reputation_min}-${reward.reputation_max}`;
        return `${amt} Rep`;
    }
    return reward.reward_type.label;
}

export function AdventuresTab({
    adventures,
    hexId,
    inProgressCount,
    statLabels,
    skillLabels,
    onTabChange,
}: AdventuresTabProps) {
    return (
        <div className="max-w-6xl">
            {/* Header - styled like a tavern sign */}
            <div className="flex items-center justify-between mb-8">
                <div className="text-center flex-1">
                    <h2 className="text-3xl font-serif font-bold text-amber-100 tracking-wide drop-shadow-lg">
                        ⚔ Quest Board ⚔
                    </h2>
                    <p className="text-amber-200/60 text-sm mt-1 font-serif italic">
                        Heroes wanted — Seek glory and fortune
                    </p>
                </div>
                {inProgressCount > 0 && (
                    <button
                        onClick={() => onTabChange('attempts')}
                        className="px-4 py-2 bg-amber-700 hover:bg-amber-600 text-amber-100 rounded border-2 border-amber-600 transition-colors font-serif"
                    >
                        My Quests ({inProgressCount})
                    </button>
                )}
            </div>

            {/* Job Board - wooden background with papers */}
            {adventures.length === 0 ? (
                <div
                    className="rounded-lg p-12 text-center"
                    style={{
                        background: 'linear-gradient(180deg, #5c4033 0%, #4a3429 50%, #3d2a1f 100%)',
                        boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.5), 0 4px 20px rgba(0,0,0,0.3)',
                        border: '4px solid #3d2a1f',
                    }}
                >
                    <p className="text-amber-200/70 font-serif text-lg italic">
                        The board is empty...
                    </p>
                    <p className="text-amber-200/50 text-sm mt-2 font-serif">
                        Check back later for new postings!
                    </p>
                </div>
            ) : (
                <div
                    className="rounded-lg p-8 relative"
                    style={{
                        background: 'linear-gradient(180deg, #5c4033 0%, #4a3429 50%, #3d2a1f 100%)',
                        boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.5), 0 4px 20px rgba(0,0,0,0.3)',
                        border: '4px solid #3d2a1f',
                    }}
                >
                    {/* Wood grain texture overlay */}
                    <div
                        className="absolute inset-0 rounded-lg pointer-events-none opacity-10"
                        style={{
                            backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(0,0,0,0.1) 50px, rgba(0,0,0,0.1) 100px)',
                        }}
                    />

                    {/* Papers Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
                        {adventures.map((adventure) => (
                            <QuestPaper
                                key={adventure.id}
                                adventure={adventure}
                                hexId={hexId}
                                rotation={getCardRotation(adventure.id)}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * Individual quest paper - looks like a notice pinned to a board
 */
function QuestPaper({
    adventure,
    hexId,
    rotation,
}: {
    adventure: Adventure;
    hexId: number;
    rotation: number;
}) {
    const difficulty = getDifficultyInfo(adventure.total_difficulty);
    const guaranteedRewards = adventure.rewards?.filter(r => r.chance_percent === 100) || [];
    const bonusRewards = adventure.rewards?.filter(r => r.chance_percent < 100) || [];

    // Calculate duration: multi-stage = 10 min per stage, single-stage = estimated_minutes
    const estimatedMinutes = adventure.is_multi_stage
        ? (adventure.stage_count || 1) * 10
        : (adventure.estimated_minutes || 10);

    return (
        <Link
            href={`/hex/${hexId}?tab=adventure-detail&adventureId=${adventure.id}`}
            className="block relative group"
            style={{
                transform: `rotate(${rotation}deg)`,
                transition: 'transform 0.3s ease',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(0deg) scale(1.02)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = `rotate(${rotation}deg)`;
            }}
        >
            {/* Pin/Nail */}
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-20">
                <div
                    className="w-4 h-4 rounded-full shadow-lg"
                    style={{
                        background: 'radial-gradient(circle at 30% 30%, #8b7355 0%, #5c4a3a 50%, #3d3029 100%)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.2)',
                    }}
                />
            </div>

            {/* Paper */}
            <div
                className="relative overflow-hidden rounded-sm"
                style={{
                    background: 'linear-gradient(135deg, #f5edd6 0%, #e8dcc4 50%, #f0e6d0 100%)',
                    boxShadow: '4px 4px 12px rgba(0,0,0,0.4), -1px -1px 4px rgba(0,0,0,0.1)',
                    border: '1px solid #c9b896',
                }}
            >
                {/* Torn/aged edge effect at top */}
                <div
                    className="absolute top-0 left-0 right-0 h-2 pointer-events-none"
                    style={{
                        background: 'linear-gradient(180deg, rgba(139,115,85,0.2) 0%, transparent 100%)',
                    }}
                />

                {/* Difficulty Ribbon - corner fold style */}
                <div
                    className="absolute top-0 right-0 w-20 h-20 overflow-hidden pointer-events-none"
                >
                    <div
                        className="absolute top-3 -right-6 w-24 text-center py-1 font-serif text-xs font-bold uppercase tracking-wide shadow-md"
                        style={{
                            transform: 'rotate(45deg)',
                            background: difficulty.ribbonColor,
                            color: difficulty.textColor,
                        }}
                    >
                        {difficulty.label}
                    </div>
                </div>

                {/* Content */}
                <div className="p-5 pt-4">
                    {/* Title */}
                    <h3 className="text-lg font-serif font-bold text-amber-900 mb-2 pr-12 leading-tight group-hover:text-amber-700 transition-colors">
                        {adventure.name}
                    </h3>

                    {/* Decorative line */}
                    <div className="flex items-center gap-2 mb-3">
                        <div className="h-px flex-1 bg-amber-800/30" />
                        <span className="text-amber-800/40 text-xs">❧</span>
                        <div className="h-px flex-1 bg-amber-800/30" />
                    </div>

                    {/* Description */}
                    {adventure.description && (
                        <p className="text-amber-900/70 text-sm font-serif leading-relaxed mb-4 line-clamp-3">
                            {adventure.description.replace(/<[^>]*>/g, '').substring(0, 120)}
                            {adventure.description.length > 120 ? '...' : ''}
                        </p>
                    )}

                    {/* Rewards Section */}
                    {(guaranteedRewards.length > 0 || bonusRewards.length > 0) && (
                        <div className="mb-3">
                            <div className="text-xs font-serif text-amber-800/60 uppercase tracking-wide mb-1">
                                Reward
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {guaranteedRewards.slice(0, 3).map((reward, idx) => (
                                    <span
                                        key={idx}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 border border-amber-300 rounded text-xs font-serif text-amber-800"
                                    >
                                        <span>{reward.reward_type.icon}</span>
                                        <span>{formatReward(reward)}</span>
                                    </span>
                                ))}
                                {bonusRewards.length > 0 && (
                                    <span className="text-xs font-serif text-amber-700/60 italic self-center">
                                        +{bonusRewards.length} bonus
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Footer - Duration and Multi-stage Badge */}
                    <div className="flex items-center justify-between pt-2 border-t border-amber-800/20">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-serif text-amber-800/50">
                                ~{estimatedMinutes} min
                            </span>
                            {adventure.is_multi_stage && (
                                <span className="px-1.5 py-0.5 bg-purple-700 text-white text-xs font-serif rounded">
                                    {adventure.stage_count} stages
                                </span>
                            )}
                        </div>
                        <span className="text-xs font-serif text-amber-800/50 italic">
                            Click to view details →
                        </span>
                    </div>
                </div>

                {/* Paper texture/grain */}
                <div
                    className="absolute inset-0 pointer-events-none opacity-30"
                    style={{
                        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.4\'/%3E%3C/svg%3E")',
                    }}
                />
            </div>

            {/* Hover shadow enhancement */}
            <div className="absolute inset-0 rounded-sm bg-black/0 group-hover:bg-black/5 transition-colors pointer-events-none" />
        </Link>
    );
}
